import { useCallback, useEffect, useState } from 'react';

export type SectionGoalKey = 'strength' | 'cardio' | 'diet' | 'medical';

export interface SectionGoal {
  target: number;
  current: number;
  unit: string;
  label: string;
}

export const SECTION_DEFAULTS: Record<SectionGoalKey, SectionGoal> = {
  strength: { target: 12, current: 0, unit: 'workouts', label: 'Workouts this month' },
  cardio: { target: 12, current: 0, unit: 'sessions', label: 'Sessions this month' },
  diet: { target: 30, current: 0, unit: 'days', label: 'Days on plan' },
  medical: { target: 4, current: 0, unit: 'tests', label: 'Tests done this year' },
};

function storageKey(section: SectionGoalKey): string {
  return 'ef_goal_' + section;
}

function load(section: SectionGoalKey): SectionGoal {
  const defaults = SECTION_DEFAULTS[section];
  try {
    const raw = localStorage.getItem(storageKey(section));
    if (!raw) return { ...defaults };
    const parsed = JSON.parse(raw) as Partial<SectionGoal>;
    return { ...defaults, ...parsed };
  } catch {
    return { ...defaults };
  }
}

export function useSectionGoal(section: SectionGoalKey) {
  const [goal, setGoal] = useState<SectionGoal>(() => load(section));

  useEffect(() => {
    setGoal(load(section));
  }, [section]);

  const save = useCallback(
    (next: { target: number; current: number }) => {
      setGoal((prev) => {
        const merged: SectionGoal = { ...prev, ...next };
        localStorage.setItem(storageKey(section), JSON.stringify(merged));
        return merged;
      });
    },
    [section],
  );

  return { goal, save };
}
